import { useNavigate } from "react-router-dom";
import { Card, PageError, PageLoading } from "../components/index.js";
import { useApi } from "../hooks/useApi.js";
import { timeAgo } from "../lib/time.js";

/**
 * Agency Home — SUPER_ADMIN/ADMIN landing. Layout B (pipeline-forward):
 * attention strip → pipeline funnel band → portfolio table → activity rail.
 * Contracts/renewals lit up in 4b-1; sections with no data (billing) are
 * simply absent.
 */
const HEALTH_DOT = { green: "#16a34a", yellow: "#e0a800", red: "#dc2626" };
const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const STAGES = [
  { key: "prospect", label: "Prospect" },
  { key: "proposal_sent", label: "Proposal" },
  { key: "negotiating", label: "Negotiating" },
  { key: "contract_sent", label: "Contract" },
];

export function AgencyHome() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi("/api/agency/home");

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !data) return <PageLoading />;

  const { attention, renewalsThisMonth, pipeline, portfolio, recentActivity } = data;
  const expiringContracts = attention?.contractsExpiringSoon || [];
  const hasAttention =
    (attention?.syncFailures?.length || 0) +
    (attention?.welcomeEmailsPending?.length || 0) +
    expiringContracts.length +
    ((attention?.billingPending?.overdue?.count || 0) > 0 ? 1 : 0) +
    ((attention?.work?.overdueTasks || 0) > 0 ? 1 : 0) > 0;

  return (
    <div className="max-w-[1200px] space-y-5">
      {hasAttention && (
        <div className="rounded-card border border-[#f0b890] bg-[#fbeae0] px-4 py-3 text-[13px] text-[#8a4b1a] flex flex-wrap gap-x-6 gap-y-1">
          {expiringContracts.length > 0 && (
            <button type="button" onClick={() => navigate("/agency/contracts")} className="hover:underline">
              ⏳ {expiringContracts.length} contract{expiringContracts.length === 1 ? "" : "s"} expiring soon
            </button>
          )}
          {attention.syncFailures.length > 0 && (
            <span>⚠ {attention.syncFailures.length} sync failure{attention.syncFailures.length === 1 ? "" : "s"}</span>
          )}
          {attention.welcomeEmailsPending.length > 0 && (
            <span>✉ {attention.welcomeEmailsPending.length} welcome email{attention.welcomeEmailsPending.length === 1 ? "" : "s"} pending</span>
          )}
          {attention?.billingPending?.overdue?.count > 0 && (
            <button type="button" onClick={() => navigate("/agency/billing?tab=awaiting")} className="hover:underline">
              💵 {attention.billingPending.overdue.count} overdue invoice{attention.billingPending.overdue.count === 1 ? "" : "s"} ({fmtMoney(attention.billingPending.overdue.total)})
            </button>
          )}
          {attention?.work?.overdueTasks > 0 && (
            <button type="button" onClick={() => navigate("/agency/work")} className="hover:underline">🛠 {attention.work.overdueTasks} overdue task{attention.work.overdueTasks === 1 ? "" : "s"}</button>
          )}
        </div>
      )}

      {/* Pipeline funnel band */}
      <Card className="p-0">
        <div className="px-5 py-3 flex items-center justify-between border-b border-border-light">
          <h2 className="text-[14px] font-semibold text-text-primary">New Business Pipeline</h2>
          <button type="button" onClick={() => navigate("/agency/pipeline")} className="text-[12px] text-accent hover:underline font-medium">View Pipeline →</button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {STAGES.map((s) => (
            <button key={s.key} type="button" onClick={() => navigate("/agency/pipeline")}
              className="rounded-card border border-border-default px-4 py-3 text-left hover:bg-app-bg transition-colors">
              <div className="text-[22px] font-semibold text-text-primary">{pipeline?.[s.key] ?? 0}</div>
              <div className="text-[12px] text-text-secondary">{s.label}</div>
            </button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* Portfolio */}
        <Card className="p-0">
          <div className="px-5 py-3 border-b border-border-light">
            <h2 className="text-[14px] font-semibold text-text-primary">Portfolio</h2>
            <p className="text-[12px] text-text-muted mt-0.5">{portfolio.length} clients · sorted by sync health</p>
          </div>
          <div className="divide-y divide-border-light">
            {/* Portfolio rows open the client's staff cockpit — the dashboards
                stay one click away via the cockpit's "View as client". */}
            {portfolio.map((c) => (
              <button key={c.id} type="button" onClick={() => navigate(`/clients/${c.id}`)}
                className="w-full px-5 py-2.5 flex items-center gap-3 text-left hover:bg-app-bg transition-colors">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: HEALTH_DOT[c.syncHealth] || "#a3a3a3" }} />
                <span className="text-[13px] font-medium text-text-primary truncate flex-1">{c.name}</span>
                {/* 4b-1: live contract value + read-time expiry state */}
                {c.contractStatus === "expiring" && (
                  <span className="text-[11px] font-medium text-[#8a4b1a]">renews {fmtDate(c.renewalDate)}</span>
                )}
                {c.contractStatus === "expired" && (
                  <span className="text-[11px] font-medium text-[#dc2626]">expired</span>
                )}
                {c.totalMonthlyValue != null && (
                  <span className="text-[11px] text-text-secondary" style={{ fontFeatureSettings: "'tnum'" }}>
                    {fmtMoney(c.totalMonthlyValue)}/mo
                  </span>
                )}
                <span className="text-[11px] text-text-muted">{c.sourcesConnected} src</span>
                <span className="text-[11px] text-text-muted w-20 text-right">{c.lastSyncAt ? timeAgo(c.lastSyncAt) : "never"}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Right rail: renewals (4b-1, absent when none) + recent activity */}
        <div className="space-y-5">
          <Card className="p-0">
            <div className="px-5 py-3 border-b border-border-light flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-text-primary">Work</h2>
              <button type="button" onClick={() => navigate("/agency/work")} className="text-[12px] text-accent hover:underline font-medium">Open →</button>
            </div>
            <div className="px-5 py-4 grid grid-cols-2 gap-3 text-[12px]">
              <div><div className="text-text-muted">Open work orders</div><div className="text-[18px] font-semibold text-text-primary">{attention?.work?.open ?? 0}</div></div>
              <div><div className="text-text-muted">Overdue tasks</div><div className={`text-[18px] font-semibold ${attention?.work?.overdueTasks > 0 ? "text-negative" : "text-text-primary"}`}>{attention?.work?.overdueTasks ?? 0}</div></div>
            </div>
          </Card>
          <Card className="p-0">
            <div className="px-5 py-3 border-b border-border-light flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-text-primary">Billing</h2>
              <button type="button" onClick={() => navigate("/agency/billing")} className="text-[12px] text-accent hover:underline font-medium">Open →</button>
            </div>
            <div className="px-5 py-4 grid grid-cols-2 gap-3 text-[12px]">
              <div><div className="text-text-muted">To invoice (upcoming)</div><div className="text-[18px] font-semibold text-text-primary">{fmtMoney(attention?.billingPending?.toInvoice?.total)}</div><div className="text-text-secondary">{attention?.billingPending?.toInvoice?.count ?? 0} items</div></div>
              <div><div className="text-text-muted">Overdue</div><div className="text-[18px] font-semibold text-text-primary">{fmtMoney(attention?.billingPending?.overdue?.total)}</div><div className="text-text-secondary">{attention?.billingPending?.overdue?.count ?? 0} invoices</div></div>
            </div>
          </Card>
        {renewalsThisMonth?.length > 0 && (
          <Card className="p-0">
            <div className="px-5 py-3 border-b border-border-light flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-text-primary">Renewals This Month</h2>
              <button type="button" onClick={() => navigate("/agency/contracts")}
                className="text-[12px] text-accent hover:underline font-medium">All →</button>
            </div>
            <div className="divide-y divide-border-light">
              {renewalsThisMonth.map((r) => (
                <button key={r.contractId} type="button" onClick={() => navigate(`/clients/${r.clientId}`)}
                  className="w-full px-5 py-2.5 flex items-center gap-2 text-left hover:bg-app-bg transition-colors">
                  <span className="text-[12px] font-medium text-text-primary truncate flex-1">{r.clientName || "—"}</span>
                  <span className="text-[11px] text-text-secondary" style={{ fontFeatureSettings: "'tnum'" }}>
                    {fmtMoney(r.monthlyValue)}/mo
                  </span>
                  <span className="text-[11px] text-text-muted">{fmtDate(r.renewalDate)}</span>
                </button>
              ))}
            </div>
          </Card>
        )}
        <Card className="p-0">
          <div className="px-5 py-3 border-b border-border-light">
            <h2 className="text-[14px] font-semibold text-text-primary">Recent Activity</h2>
          </div>
          <div className="px-5 py-3 space-y-2">
            {recentActivity.length === 0 && <p className="text-[12px] text-text-muted">No recent activity.</p>}
            {recentActivity.map((a, i) => (
              <div key={i} className="text-[12px] text-text-secondary">
                <span className="text-text-primary">{a.description}</span>
                <span className="text-text-muted"> · {timeAgo(a.occurredAt)}</span>
              </div>
            ))}
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
}
